/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { useEffect } from "react";

import { ModKey } from "src/lib/types";

export const useHotkey = (
  hotkey: { key: string; modifiers: ModKey[] },
  handler: (e: KeyboardEvent) => void,
  dependencies: any[] = [],
  options: { preventDefault: boolean; stopPropagation?: boolean } = {
    preventDefault: false,
    stopPropagation: false,
  }
): void => {
  // handler for a keydown event:
  const listener = (e: KeyboardEvent) => {
    // Check if the modifiers are pressed, if not exit:
    if (
      (hotkey.modifiers.includes("shift") && !e.shiftKey) ||
      (!hotkey.modifiers.includes("shift") && e.shiftKey)
    ) {
      return;
    }
    if ((hotkey.modifiers.includes("alt") && !e.altKey) || (!hotkey.modifiers.includes("alt") && e.altKey)) {
      return;
    }
    if ((hotkey.modifiers.includes("ctrl") && !e.ctrlKey) || (!hotkey.modifiers.includes("ctrl") && e.ctrlKey)) {
      return;
    }
    if ((hotkey.modifiers.includes("meta") && !e.metaKey) || (!hotkey.modifiers.includes("meta") && e.metaKey)) {
      return;
    }
    if (e.key?.toUpperCase() === hotkey.key.toUpperCase()) {
      // Check if the key is pressed (check upper case too incase shift is pressed):
      // Stop propagation if necessary:
      if (options.stopPropagation) {
        e.stopPropagation();
      }
      // Prevent default if necessary:
      if (options.preventDefault) {
        e.preventDefault();
      }

      // Call the callback:
      handler(e);
    }
  };
  // useEffect which mounts according to the params:
  useEffect(() => {
    // On mount add event listener:
    window.addEventListener("keydown", listener);
    // On unmount remove event listener:
    return () => window.removeEventListener("keydown", listener);
  }, [handler, ...dependencies]);
};
